import { readImageSize, readVideoSize, validateCatalogPrice } from './profileFrameRules';

/**
 * Animated profile backgrounds: `background_animated` (CategoryKey PROFILE).
 * Shares the user's one Background slot with static `background` in the app.
 * Mirrors tezda-wallet's server-side validation (tezda-service-library
 * utils/profileBackground.ts) — the server re-checks everything; this just
 * fails fast in the form.
 *
 * Media slots (stored as MediaAssets, returned to the app as BackgroundAssets):
 *   webm      → android   (.webm, video/webm)        required
 *   ios_alpha → ios       (.mov, video/quicktime)     required
 *   thumbnail → thumbnail (.webp, image/webp)         required
 *   all slots: same aspect ratio, max 2160 px on the long side
 */

export const ANIMATED_BACKGROUND_TYPE = 'background_animated';
export const isAnimatedBackgroundType = (type: string) => type === ANIMATED_BACKGROUND_TYPE;

export const ANIMATED_BACKGROUND_LIMITS = {
  maxVideoBytes: 15 * 1024 * 1024,
  maxImageBytes: 1 * 1024 * 1024,
  maxDimension: 2160,
};

type BackgroundAsset = { Type: string; Url: string; Width?: number; Height?: number };

const EXPECTED_MIME: Record<string, string> = {
  webm: 'video/webm',
  ios_alpha: 'video/quicktime',
  thumbnail: 'image/webp',
};
const EXPECTED_EXT: Record<string, string> = { webm: 'webm', ios_alpha: 'mov', thumbnail: 'webp' };
const SLOT_LABEL: Record<string, string> = { webm: 'Android', ios_alpha: 'iOS', thumbnail: 'Thumbnail' };

const mb = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(bytes < 1024 * 1024 ? 2 : 0)} MB`;
const extOf = (name: string) => name.split(/[?#]/)[0].split('.').pop()?.toLowerCase();

/**
 * Checks one file queued for an animated-background slot: MIME type, file
 * extension, size and max dimensions. Returns the measured Width/Height so the
 * same-aspect-ratio rule can be checked on save.
 */
export async function inspectAnimatedBackgroundFile(
  mediaType: string,
  file: File
): Promise<{ Width?: number; Height?: number }> {
  const expectedMime = EXPECTED_MIME[mediaType];
  if (!expectedMime) {
    throw new Error('Animated backgrounds only take Android (.webm), iOS (.mov) and Thumbnail (.webp) files.');
  }
  const label = SLOT_LABEL[mediaType];
  if (extOf(file.name) !== EXPECTED_EXT[mediaType]) {
    throw new Error(`${label} file must be a .${EXPECTED_EXT[mediaType]} file (got ${file.name}).`);
  }
  // Browsers sometimes report an empty type for .mov/.webm; the extension check above still applies.
  if (file.type && file.type !== expectedMime) {
    throw new Error(`${label} file must be ${expectedMime} (got ${file.type}).`);
  }

  const isVideo = mediaType !== 'thumbnail';
  const maxBytes = isVideo ? ANIMATED_BACKGROUND_LIMITS.maxVideoBytes : ANIMATED_BACKGROUND_LIMITS.maxImageBytes;
  if (file.size > maxBytes) {
    throw new Error(`${file.name} is ${mb(file.size)}; the limit for ${label} is ${mb(maxBytes)}.`);
  }

  const size = isVideo ? await readVideoSize(file) : await readImageSize(file);
  if (!size) return {};
  const max = ANIMATED_BACKGROUND_LIMITS.maxDimension;
  if (size.Width > max || size.Height > max) {
    throw new Error(`${file.name} is ${size.Width}×${size.Height}; animated backgrounds are limited to ${max} px per side.`);
  }
  return size;
}

/** Throws with a form-facing message when an animated background product isn't valid. */
export function validateAnimatedBackgroundProduct(input: {
  Type: string;
  CurrencyType: 'coin' | 'token';
  PriceCoins?: number;
  PriceTokens?: number;
  IsActive?: boolean;
  Metadata?: Record<string, any> | null;
  MediaAssets: BackgroundAsset[];
}) {
  if (!isAnimatedBackgroundType(input.Type)) return;

  validateCatalogPrice('animated backgrounds', input);

  // Selected by Type, never by array position.
  const byType = new Map(input.MediaAssets.map((a) => [a.Type, a]));
  for (const slot of ['webm', 'ios_alpha', 'thumbnail']) {
    const asset = byType.get(slot);
    if (!asset) throw new Error(`Animated backgrounds require a ${SLOT_LABEL[slot]} file (.${EXPECTED_EXT[slot]}).`);
    if (!/^https:\/\//i.test(asset.Url)) throw new Error(`${SLOT_LABEL[slot]} URL must use HTTPS.`);
    if (extOf(asset.Url) !== EXPECTED_EXT[slot]) {
      throw new Error(`${SLOT_LABEL[slot]} file must be a .${EXPECTED_EXT[slot]} file.`);
    }
  }

  const sized = input.MediaAssets.filter((a) => a.Width && a.Height);
  const ratio = (a: BackgroundAsset) => (a.Width as number) / (a.Height as number);
  const mismatch = sized.find((a) => Math.abs(ratio(a) - ratio(sized[0])) / ratio(sized[0]) > 0.02);
  if (mismatch) {
    throw new Error(
      `Thumbnail and videos must have matching dimensions (${SLOT_LABEL[sized[0].Type]} is ${sized[0].Width}×${sized[0].Height}, ` +
        `${SLOT_LABEL[mismatch.Type]} is ${mismatch.Width}×${mismatch.Height}).`
    );
  }
}

/** Thumbnail first: old app versions render MediaAssets[0] as the background image. */
export function orderAnimatedBackgroundMediaAssets<T extends { Type: string }>(type: string, assets: T[]): T[] {
  if (!isAnimatedBackgroundType(type)) return assets;
  return [...assets.filter((a) => a.Type === 'thumbnail'), ...assets.filter((a) => a.Type !== 'thumbnail')];
}
